
import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { LucideIcon } from 'lucide-react';

interface ActionCardProps {
  title: string;
  description: string;
  features: string[];
  buttonText: string;
  buttonIcon: LucideIcon;
  secondaryIcon: LucideIcon;
  gradientFrom: string;
  gradientTo: string;
  featureColor: string;
  shadowColor: string;
  animationDelay: number;
  animationDirection: number;
  iconRotation: number;
  onButtonClick?: () => void;
}

const ActionCard = ({
  title,
  description,
  features,
  buttonText,
  buttonIcon: ButtonIcon,
  secondaryIcon: SecondaryIcon,
  gradientFrom,
  gradientTo,
  featureColor,
  shadowColor,
  animationDelay,
  animationDirection,
  iconRotation,
  onButtonClick
}: ActionCardProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, x: animationDirection * 50 }}
      whileInView={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.8, delay: animationDelay }}
      viewport={{ once: true }}
      className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 hover:border-white/40 transition-all duration-300"
    >
      <div className="text-center mb-8">
        <motion.div
          whileHover={{ scale: 1.1, rotate: iconRotation }}
          className={`inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r ${gradientFrom} ${gradientTo} rounded-2xl mb-6`}
        >
          <SecondaryIcon className="w-10 h-10 text-white" />
        </motion.div>
        <h3 className="text-3xl font-bold text-white mb-4">
          {title}
        </h3>
        <p className="text-gray-300 leading-relaxed mb-6">
          {description}
        </p>
      </div>
      
      <div className="space-y-3 mb-8">
        {features.map((feature, index) => (
          <div key={index} className="flex items-center text-gray-300">
            <div className={`w-2 h-2 ${featureColor} rounded-full mr-3`}></div>
            <span>{feature}</span>
          </div>
        ))}
      </div>

      <Button 
        onClick={onButtonClick}
        size="lg"
        className={`w-full bg-gradient-to-r ${gradientFrom} ${gradientTo} hover:${gradientFrom.replace('600', '700')} hover:${gradientTo.replace('600', '700')} text-white py-4 text-lg font-semibold rounded-2xl shadow-2xl hover:${shadowColor} transition-all duration-300`}
      >
        <ButtonIcon className="w-5 h-5 mr-2" />
        {buttonText}
        <SecondaryIcon className="w-5 h-5 ml-2" />
      </Button>
    </motion.div>
  );
};

export default ActionCard;
